import type { Timestamp } from "firebase/firestore";

export function formatTime(time24: string | null | undefined): string {
  if (!time24) return "";
  const [hourStr, minuteStr] = time24.split(":");
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || "00";
  if (isNaN(hour)) return time24;
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minute} ${period} (${time24})`;
}

export interface FormatDateOptions {
  withWeekday?: boolean;
  withYear?: boolean;
}

// Formats a YYYY-MM-DD calendar date. The T00:00:00 suffix anchors the date in
// local time so the displayed day matches what was stored, regardless of TZ.
export function formatDate(
  dateStr: string | null | undefined,
  opts: FormatDateOptions = {}
): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  const formatOpts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (opts.withYear !== false) formatOpts.year = "numeric";
  if (opts.withWeekday) formatOpts.weekday = "short";
  return d.toLocaleDateString("en-US", formatOpts);
}

type MaybeTimestamp = Timestamp | Date | string | number | null | undefined;

export interface FormatTimestampOptions {
  withTime?: boolean;
}

export function formatTimestamp(
  ts: MaybeTimestamp,
  opts: FormatTimestampOptions = {}
): string {
  if (!ts) return "—";
  const d = typeof (ts as Timestamp).toDate === "function" ? (ts as Timestamp).toDate() : new Date(ts as Date | string | number);
  let s = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (opts.withTime) {
    s += " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  return s;
}

export function timeAgo(ts: MaybeTimestamp): string {
  if (!ts) return "—";
  const d = typeof (ts as Timestamp).toDate === "function" ? (ts as Timestamp).toDate() : new Date(ts as Date | string | number);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
